import { useCallback, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../api/client';
import { usersAPI } from '../api/users';
import { ModalSheet } from '../components/ui/ModalSheet';
import { Button } from '../components/ui/Button';
import { ThemedField, ThemedInput } from '../components/ui/ThemedSurface';
import { COLORS } from '@/constants';

/**
 * Gate navigation behind transaction password.
 * Always prompts — no session memory (refresh / next click requires password again).
 */
export function useTxPasswordGate(opts?: {
  title?: string;
  hint?: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState<(() => void) | null>(null);
  const [txPassword, setTxPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const gate = useCallback((action: () => void) => {
    setPending(() => action);
    setTxPassword('');
    setError(null);
    setOpen(true);
  }, []);

  async function submit() {
    const pwd = txPassword.trim();
    if (!pwd) {
      setError(t('common.enterTxPassword', '请输入交易密码'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await usersAPI.verifyTxPassword({ transaction_password: pwd });
      setOpen(false);
      setTxPassword('');
      const next = pending;
      setPending(null);
      next?.();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('common.error', '操作失败'));
    } finally {
      setBusy(false);
    }
  }

  const modal: ReactNode = (
    <ModalSheet
      open={open}
      onClose={() => {
        if (busy) return;
        setOpen(false);
        setPending(null);
        setTxPassword('');
        setError(null);
      }}
      title={opts?.title ?? t('common.txPassword', '交易密码')}
      footer={
        <Button
          type="button"
          fullWidth
          isLoading={busy}
          disabled={!txPassword.trim()}
          onClick={() => void submit()}
        >
          {t('common.confirm', '确认')}
        </Button>
      }
    >
      <div className="flex flex-col gap-3 py-1">
        <p className="text-xs leading-relaxed" style={{ color: COLORS.iconColor }}>
          {opts?.hint ?? t('common.enterTxPasswordToContinue', '请输入交易密码后继续')}
        </p>
        <ThemedField label={t('common.txPassword', '交易密码')} required>
          <ThemedInput
            type="password"
            autoComplete="current-password"
            value={txPassword}
            onChange={(e) => setTxPassword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void submit();
            }}
            placeholder={t('common.enterTxPassword', '请输入交易密码')}
          />
        </ThemedField>
        {error ? <p className="text-sm text-negative">{error}</p> : null}
      </div>
    </ModalSheet>
  );

  return { gate, modal };
}
